/**
 * Calls the core API through /api (the Vite proxy in development, Caddy when deployed). Adds the bearer
 * token and the current tenant, and turns error responses into ApiError with the fields at fault.
 */
export interface FieldIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string | null,
    readonly issues: FieldIssue[],
  ) {
    super(message);
  }
}

let session: { token: string | null; tenantId: string | null } = { token: null, tenantId: null };
let onUnauthorized: () => void = () => {};

export function setSession(token: string | null, tenantId: string | null) {
  session = { token, tenantId };
}

export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

function send(method: string, path: string, body?: unknown, accept = 'application/json'): Promise<Response> {
  const headers: Record<string, string> = { accept };
  if (session.token) headers.authorization = `Bearer ${session.token}`;
  if (session.tenantId) headers['x-tenant-id'] = session.tenantId;
  // A FormData body (a file upload) sets its own multipart content type.
  const form = body instanceof FormData;
  if (body !== undefined && !form) headers['content-type'] = 'application/json';
  return fetch(`/api${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : form ? body : JSON.stringify(body),
  }).catch(() => {
    throw new ApiError(0, 'Could not reach the server. Check your connection.', 'network', []);
  });
}

/** JSON in and out; a FormData body is sent as multipart (uploads). */
export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await send(method, path, body);
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (res.ok) return data as T;
  throw failure(res, data);
}

/** A file from the API (e.g. a stored document), as a Blob. */
export async function apiBlob(path: string): Promise<Blob> {
  const res = await send('GET', path, undefined, '*/*');
  if (res.ok) return res.blob();
  throw failure(res, await res.json().catch(() => null));
}

function failure(res: Response, data: { message?: unknown; error?: string; issues?: FieldIssue[] } | null) {
  if (res.status === 401) onUnauthorized();
  const message =
    (typeof data?.message === 'string' && data.message) ||
    (Array.isArray(data?.message) && data.message.join('; ')) ||
    `Request failed (${res.status})`;
  return new ApiError(
    res.status,
    message,
    data?.error ?? null,
    Array.isArray(data?.issues) ? data.issues : [],
  );
}
